/**
 * "Is this machine keeping up?" — one answer, shared (CAM-06).
 *
 * Two objects can produce pose frames: the `PoseTracker` (a loop over any `PoseSource`) and the
 * `CameraSession` (the app's actual detection loop, D-063(4)). Both have to answer the same
 * question, and the question has exactly one right answer — so the window, the mean and the
 * threshold comparison live here rather than being written out twice and drifting.
 *
 * The rule itself is deliberately slow to fire: one slow frame is a hiccup, a whole window of them
 * is a slow computer. A signal that fired on a single stalled frame would degrade a session every
 * time a browser garbage-collected.
 */

import type { PoseTunables } from '../../shared/types/tunables';

/**
 * How many detections the signal averages over.
 *
 * A code-level smoothing constant rather than a tunable, on purpose: what a grown-up might one day
 * tune is the *threshold* (`pose.lowPerformanceFrameMs`), not the shape of the average.
 */
export const FRAME_TIME_WINDOW = 10;

/** Adds one latency to the window, keeping only the most recent {@link FRAME_TIME_WINDOW}. */
export function recordFrameTime(window: readonly number[], latencyMs: number): number[] {
  return [...window, latencyMs].slice(-FRAME_TIME_WINDOW);
}

/** The mean of the window, or null before any frame has been seen. */
export function meanFrameTime(window: readonly number[]): number | null {
  if (window.length === 0) return null;
  return window.reduce((total, value) => total + value, 0) / window.length;
}

/** The CAM-06 signal: a full window whose mean is over the flagged `lowPerformanceFrameMs`. */
export function isLowPerformance(window: readonly number[], pose: PoseTunables): boolean {
  const mean = meanFrameTime(window);
  return window.length >= FRAME_TIME_WINDOW && mean !== null && mean > pose.lowPerformanceFrameMs;
}
